import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Download, Lock } from "lucide-react";

const CATEGORIES = ["Bylaws", "Rules", "Minutes", "Financials", "General"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DocumentsPage() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("All");

  const load = async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch {
      toast.error("Failed to load documents");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "All" ? docs : docs.filter((d) => d.category === filter);

  const buildDownloadUrl = (d) => {
    if (d.storage_path) return `${BACKEND}/api/documents/download/${d.storage_path}`;
    return d.url;
  };

  return (
    <div data-testid="documents-page" className="max-w-6xl mx-auto px-6 py-16">
      <div className="mb-10">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Library</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A2421]">Documents</h1>
        <p className="text-[#5C6A64] mt-3 max-w-2xl">
          Bylaws, rules, meeting minutes, and financial statements — everything the HOA publishes for member reference.
        </p>
      </div>

      {!user && (
        <div className="mb-8 p-4 bg-[#EADCC8] border border-[#D5CFC4] rounded-2xl text-sm text-[#1A2421] flex items-center gap-3">
          <Lock className="w-4 h-4 text-[#C86650]" />
          Only public documents are shown. <a href="/login" className="underline font-medium">Sign in</a> to access bylaws, minutes, and financials.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-8">
        {["All", ...CATEGORIES].map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase()}`}
            onClick={() => setFilter(c)}
            className={`px-5 py-2 rounded-full text-sm border transition-colors ${
              filter === c
                ? "bg-[#2C4C3B] text-white border-[#2C4C3B]"
                : "bg-white border-[#D5CFC4] text-[#1A2421] hover:bg-[#EADCC8]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((d) => (
          <Card key={d.id} data-testid={`doc-card-${d.id}`} className="rounded-2xl border-[#D5CFC4] hover:-translate-y-1 transition-transform duration-300 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)]">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="w-11 h-11 rounded-full bg-[#EADCC8] flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#2C4C3B]" />
                </div>
                <Badge className="bg-[#2C4C3B] text-white hover:bg-[#233D2F]">{d.category}</Badge>
              </div>
              <p className="font-serif text-xl text-[#1A2421]">{d.title}</p>
              {d.description && <p className="text-sm text-[#5C6A64] mt-2 line-clamp-2">{d.description}</p>}
              {d.original_filename && <p className="text-xs text-[#5C6A64] mt-2">{d.original_filename} · {((d.size || 0) / 1024).toFixed(0)} KB</p>}
              <div className="mt-4">
                <a href={buildDownloadUrl(d)} target="_blank" rel="noreferrer" data-testid={`download-${d.id}`}>
                  <Button variant="outline" size="sm" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white">
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-[#5C6A64] py-12">No documents in this category.</div>
        )}
      </div>
    </div>
  );
}
